import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Video, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuthStore } from "@/store/authStore";
import { wellnessApi } from "@/lib/api";
import { SAMPLE_THERAPISTS, normalizeConsultant, getLocalSessions, saveLocalSessions } from "@/lib/therapists";

const SESSION_TYPES = [
  { key: "video", label: "Video Consultation", sub: "Talk securely from anywhere", Icon: Video },
  { key: "in_person", label: "In-clinic Consultation", sub: "Visit at the clinic", Icon: Building2 },
];
const TIMES = ["10:00 AM", "11:00 AM", "12:00 PM", "03:00 PM", "04:00 PM", "05:00 PM"];

function nextDays(n) {
  const out = [];
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const base = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(base); d.setDate(base.getDate() + i);
    out.push({ label: days[d.getDay()], day: d.getDate(), month: months[d.getMonth()], iso: d.toISOString().slice(0, 10) });
  }
  return out;
}

export default function WellnessBook() {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useAuthStore((s) => s.user);
  const [t, setT] = useState(null);
  const [step, setStep] = useState(1);
  const [sessionType, setSessionType] = useState("video");
  const [clinicLocation, setClinicLocation] = useState("");
  const [dateIso, setDateIso] = useState("");
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const days = useMemo(() => nextDays(7), []);

  useEffect(() => {
    const sample = SAMPLE_THERAPISTS.find((x) => x.id === id);
    if (sample) { setT(sample); return; }
    wellnessApi.consultant(id)
      .then((res) => { const c = res.data?.consultant || res.data; setT(c ? normalizeConsultant(c) : SAMPLE_THERAPISTS[0]); })
      .catch(() => setT(SAMPLE_THERAPISTS[0]));
  }, [id]);

  if (!t) return <p className="text-center text-muted-foreground py-16">Loading…</p>;

  const loc = (t.clinic_locations || []).find((l) => l.id === clinicLocation);
  const step1Invalid = step === 1 && sessionType === "in_person" && !clinicLocation;
  const step2Invalid = step === 2 && (!dateIso || !time);
  const typeLabel = SESSION_TYPES.find((s) => s.key === sessionType)?.label || "";
  const dObj = days.find((d) => d.iso === dateIso);

  const confirm = async () => {
    setSubmitting(true);
    try {
      if (!t.is_sample) {
        await wellnessApi.book({
          booking_type: "consultation",
          customer_phone: user?.phone || "",
          customer_name: user?.name || user?.full_name || "",
          consultant_id: t.id,
          consultation_mode: sessionType,
          scheduled_date: dateIso,
          scheduled_time: time,
          special_requests: loc ? `In-clinic at ${loc.name}, ${loc.address}${loc.city ? ", " + loc.city : ""}` : undefined,
        });
      }
      const prev = getLocalSessions();
      prev.unshift({ id: `local-${Date.now()}`, consultant_name: t.full_name, consultant_title: t.title, photo: t.profile_photo, mode: sessionType, date: dateIso, time, status: "confirmed", fee: t.fee, mins: t.session_mins, location: loc ? `${loc.name}, ${loc.city}` : undefined });
      saveLocalSessions(prev);
      navigate("/mental-wellness/my-sessions", { replace: true });
    } catch {
      toast.error("Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5 pb-24">
      <button onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))} className="flex items-center gap-1 text-sm font-medium" style={{ color: "#7C3AED" }} data-testid="book-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="text-center">
        <h1 className="text-xl font-bold" style={{ color: "#2E1065" }}>Book a Session</h1>
        <p className="text-sm" style={{ color: "#8B7CA6" }}>with {t.full_name}</p>
      </div>

      <div className="flex justify-around py-3 border-y" style={{ borderColor: "#F1ECFA" }}>
        {[{ n: 1, l: "Session" }, { n: 2, l: "Date & Time" }, { n: 3, l: "Confirm" }].map((s) => (
          <div key={s.n} className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold" style={step >= s.n ? { background: "#7C3AED", color: "#fff" } : { background: "#EDE7F8", color: "#8B7CA6" }}>{s.n}</span>
            <span className="text-sm font-semibold" style={{ color: step >= s.n ? "#2E1065" : "#8B7CA6" }}>{s.l}</span>
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <h2 className="font-bold" style={{ color: "#1F1147" }}>Select Session Type</h2>
          {SESSION_TYPES.filter((s) => (s.key !== "video" || t.offers_video) && (s.key !== "in_person" || t.offers_in_person)).map((s) => (
            <button key={s.key} onClick={() => setSessionType(s.key)} data-testid={`type-${s.key}`} className="w-full flex items-center gap-3 rounded-2xl border-2 p-4 text-left" style={sessionType === s.key ? { borderColor: "#7C3AED", background: "#F6F1FD" } : { borderColor: "#EDE7F8", background: "#fff" }}>
              <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: sessionType === s.key ? "#EADBFB" : "#F3EEFC" }}><s.Icon className="h-5 w-5" style={{ color: sessionType === s.key ? "#7C3AED" : "#8B7CA6" }} /></div>
              <div className="flex-1"><p className="font-bold" style={{ color: "#1F1147" }}>{s.label}</p><p className="text-sm" style={{ color: "#8B7CA6" }}>{s.sub}</p></div>
            </button>
          ))}
          {sessionType === "in_person" && (
            <div className="space-y-2 pt-2" data-testid="clinic-locations">
              <h3 className="font-bold" style={{ color: "#1F1147" }}>Select Clinic Location</h3>
              {(t.clinic_locations && t.clinic_locations.length > 0) ? t.clinic_locations.map((l) => (
                <button key={l.id} onClick={() => setClinicLocation(l.id)} data-testid={`clinic-${l.id}`} className="w-full flex items-center gap-3 rounded-2xl border-2 p-3 text-left" style={clinicLocation === l.id ? { borderColor: "#7C3AED", background: "#F6F1FD" } : { borderColor: "#EDE7F8", background: "#fff" }}>
                  <div className="flex-1"><p className="font-bold text-sm" style={{ color: "#1F1147" }}>{l.name}</p><p className="text-xs mt-0.5" style={{ color: "#8B7CA6" }}>{l.address}{l.city ? `, ${l.city}` : ""}</p></div>
                </button>
              )) : <p className="text-sm rounded-xl p-3" style={{ background: "#FFF7ED", color: "#8B7CA6" }}>No in-clinic locations listed. Please choose video consultation.</p>}
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h2 className="font-bold" style={{ color: "#1F1147" }}>Select Date</h2>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {days.map((d) => (
              <button key={d.iso} onClick={() => setDateIso(d.iso)} data-testid={`date-${d.iso}`} className="shrink-0 w-14 py-3 rounded-2xl border text-center" style={dateIso === d.iso ? { background: "#7C3AED", borderColor: "#7C3AED", color: "#fff" } : { background: "#fff", borderColor: "#EDE7F8" }}>
                <p className="text-xs font-bold" style={{ color: dateIso === d.iso ? "#fff" : "#8B7CA6" }}>{d.label}</p>
                <p className="text-lg font-black mt-1" style={{ color: dateIso === d.iso ? "#fff" : "#1F1147" }}>{d.day}</p>
              </button>
            ))}
          </div>
          <h2 className="font-bold" style={{ color: "#1F1147" }}>Select Time</h2>
          <div className="grid grid-cols-3 gap-2.5">
            {TIMES.map((tm) => (
              <button key={tm} onClick={() => setTime(tm)} data-testid={`time-${tm}`} className="py-3 rounded-xl border text-sm font-semibold" style={time === tm ? { background: "#7C3AED", borderColor: "#7C3AED", color: "#fff" } : { background: "#fff", borderColor: "#EDE7F8", color: "#53456F" }}>{tm}</button>
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <h2 className="font-bold" style={{ color: "#1F1147" }}>Confirm your booking</h2>
          <div className="rounded-2xl border bg-white px-4" style={{ borderColor: "#EDE7F8" }}>
            <Row label="Professional" value={t.full_name} />
            <Row label="Session" value={typeLabel} />
            {sessionType === "in_person" && loc && <Row label="Location" value={`${loc.name}, ${loc.city}`} />}
            <Row label="Date" value={dObj ? `${dObj.label}, ${dObj.day} ${dObj.month}` : "-"} />
            <Row label="Time" value={time || "-"} />
            <Row label="Fee" value={`₹${t.fee.toLocaleString("en-IN")} (${t.session_mins} mins)`} last />
          </div>
          {t.is_sample && <p className="text-xs italic" style={{ color: "#8B7CA6" }}>Sample professional — real bookings activate once the provider app onboards this therapist.</p>}
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 md:static md:border-0 md:p-0" style={{ borderColor: "#F1ECFA" }}>
        <button disabled={step1Invalid || step2Invalid || submitting} onClick={() => (step < 3 ? setStep(step + 1) : confirm())} data-testid="book-next" className="w-full rounded-2xl py-4 font-bold text-white" style={{ background: step1Invalid || step2Invalid ? "#C9B9E8" : "#7C3AED" }}>
          {submitting ? "Booking…" : step < 3 ? "Continue" : "Confirm Booking"}
        </button>
      </div>
    </div>
  );
}

function Row({ label, value, last }) {
  return (
    <div className="flex items-center justify-between py-3.5" style={last ? {} : { borderBottom: "1px solid #F1ECFA" }}>
      <span className="text-sm font-semibold" style={{ color: "#8B7CA6" }}>{label}</span>
      <span className="text-sm font-semibold text-right" style={{ color: "#1F1147" }}>{value}</span>
    </div>
  );
}
